'use client';
import React, { useCallback, useState } from 'react';
import { Button, Spinner } from '@skin-seoul/design-system';
import {
  CART_QUERY_KEYS,
  ProductType,
  useCartService,
  useRegionCurrency,
  type AddToCartResponse,
  type UpdateCartItemResponse,
} from '@skin-seoul/service';
import { useProductCard } from '../ProductCardContext';
import { LucideIcon } from '../../../components/LucideIcon';
import { AnimatePresence, motion } from 'motion/react';
import {
  BridgeApi,
  Condition,
  useDebouncedNumberCommit,
  useIsTouchDevice,
  useMutationLoading,
  usePageLogger,
} from '@skin-seoul/utils';

const MotionButton = motion.create(Button);

// These three controls are icon-only (LucideIcon forwards no aria-label and no
// <title>), so before this they had an accessible name of "" and were
// unreachable by role+name — an a11y gap as much as a test gap, hence the
// aria-labels alongside the testids. Note the "+" does NOT add to cart for a
// Variable product: handlePlusClick navigates to the PDP on desktop / opens the
// options panel on first touch, so a caller must branch on product type.
const PRODUCT_CARD_ADD_TO_CART_INCREMENT_TEST_ID = 'product-card-add-to-cart-increment';
const PRODUCT_CARD_ADD_TO_CART_DECREMENT_TEST_ID = 'product-card-add-to-cart-decrement';
const PRODUCT_CARD_ADD_TO_CART_QUANTITY_TEST_ID = 'product-card-add-to-cart-quantity';

type ProductCardAddToCartProps = {
  onAddToCartClick?: () => void;
  onAddToCartSuccess?: (params: { cartResponse: AddToCartResponse | UpdateCartItemResponse; quantity: number }) => void;
  className?: string;
};

const ProductCardAddToCart = ({ onAddToCartClick, onAddToCartSuccess, className }: ProductCardAddToCartProps) => {
  const { sku, productType, status, href, name, brand, originalPrice, salePrice, productId, category } =
    useProductCard();
  const isOutOfStock = status === 'outofstock';
  const { currency } = useRegionCurrency();
  const { useAddToCart, useGetCartQuery, useUpdateCartItem, useDeleteCartItem } = useCartService();
  const { data: cart } = useGetCartQuery({ currency });
  const { mutate: addToCart, isPending: isAddToCartPending } = useAddToCart({ currency });
  const { mutate: updateCartItem, isPending: isUpdateCartItemPending } = useUpdateCartItem({ currency });
  const { mutate: deleteCartItem, isPending: isDeleteCartItemPending } = useDeleteCartItem({ currency });
  const [hovered, setHovered] = useState(false);
  const isTouch = useIsTouchDevice();
  const [tappedOpen, setTappedOpen] = useState(false);
  const isCartLoading = useMutationLoading(CART_QUERY_KEYS.default());
  const logger = usePageLogger();

  const currentQuantity = cart?.cart?.item?.find((item) => item.sku === sku)?.quantity || 0;
  const isLoading = isAddToCartPending || isUpdateCartItemPending || isDeleteCartItemPending;

  const COMMIT_DEBOUNCE_MS = 300;

  const { value: displayQuantity, updateBy } = useDebouncedNumberCommit({
    initialValue: currentQuantity,
    delayMs: COMMIT_DEBOUNCE_MS,
    max: 999,
    onCommit: (nextQuantity) => {
      if (!sku || productType === ProductType.Variable || isOutOfStock) {
        return;
      }

      const itemKey = cart?.cart?.item?.find((item) => item.sku === sku)?.key;
      const addedQuantity = nextQuantity - currentQuantity;

      if (nextQuantity <= 0) {
        if (itemKey) {
          deleteCartItem(
            { itemKey, currency },
            {
              onSuccess: () => {
                if (BridgeApi.isWebview()) {
                  BridgeApi.cartUpdated();
                }
              },
            },
          );
        }
        return;
      }

      if (!itemKey) {
        addToCart(
          { sku, quantity: nextQuantity, currency },
          {
            onSuccess: (cartResponse) => {
              onAddToCartSuccess?.({ cartResponse, quantity: nextQuantity });
              if (BridgeApi.isWebview()) {
                BridgeApi.cartUpdated();
              }
            },
          },
        );
        return;
      }

      updateCartItem(
        { itemKey, quantity: nextQuantity, currency },
        {
          onSuccess: (cartResponse) => {
            if (addedQuantity > 0) {
              onAddToCartSuccess?.({ cartResponse, quantity: addedQuantity });
            }
            if (BridgeApi.isWebview()) {
              BridgeApi.cartUpdated();
            }
          },
        },
      );
    },
  });

  const openOptions = (!isTouch && hovered) || displayQuantity > 0 || isLoading || (isTouch && tappedOpen);

  const trackViewItemEvent = useCallback(() => {
    if (productType !== ProductType.Variable || !logger) {
      return;
    }

    const itemPrice = salePrice ?? originalPrice;

    logger.event('PLC_view_item', 'click', {
      name: 'PLC_view_item',
      'x-fb-cd-content_type': 'product',
      'x-fb-cd-content_name': name,
      'x-fb-cd-content_ids': productId || sku,
      value: itemPrice,
      currency,
      items: [
        {
          item_id: productId || sku,
          item_name: name,
          item_brand: brand || '',
          item_sku: sku,
          price: itemPrice.toString(),
          item_category: category?.[0] || '',
          item_category2: category?.[1] || '',
        },
      ],
      event_name: 'view_item',
      event_time: Math.round(new Date().getTime() / 1000),
    });
  }, [productType, logger, salePrice, originalPrice, productId, sku, name, brand, category, currency]);

  const trackAddToCartEvent = useCallback(
    (quantity: number) => {
      if (!logger) {
        return;
      }

      const itemPrice = salePrice ?? originalPrice;
      const totalPrice = itemPrice * quantity;

      logger.click('add_to_cart', {
        'x-fb-cd-content_type': 'product',
        'x-fb-cd-content_name': name,
        'x-fb-cd-content_ids': productId || sku,
        value: totalPrice.toString(),
        currency,
        items: [
          {
            item_id: productId || sku,
            item_name: name,
            item_brand: brand || '',
            item_sku: sku,
            price: itemPrice.toString(),
            item_category: category?.[0] || '',
            item_category2: category?.[1] || '',
          },
        ],
        event_name: 'add_to_cart',
        event_time: Math.round(new Date().getTime() / 1000),
        product_sku: sku,
      });
    },
    [logger, salePrice, originalPrice, name, productId, sku, currency, brand, category],
  );

  const handleNavigate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      window.location.href = href;
    },
    [href],
  );

  const handleAddClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!sku || isLoading || productType === ProductType.Variable || isOutOfStock) {
        return;
      }
      if (!onAddToCartSuccess) {
        trackAddToCartEvent(1);
      }
      onAddToCartClick?.();
      updateBy(1);
    },
    [sku, isLoading, productType, updateBy, trackAddToCartEvent, onAddToCartClick, onAddToCartSuccess, isOutOfStock],
  );

  const handleMinusClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!sku || isLoading) {
        return;
      }
      updateBy(-1);
    },
    [sku, isLoading, updateBy],
  );

  const handlePlusClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const isVariableProduct = productType === ProductType.Variable;
      const isFirstTapOnMobile = isTouch && !tappedOpen;
      const isSecondTapOnMobile = isTouch && tappedOpen;
      const shouldNavigateOnDesktop = !isTouch && openOptions;

      if (!isVariableProduct) {
        handleAddClick(e);
        return;
      }

      if (isFirstTapOnMobile) {
        e?.stopPropagation();
        e?.preventDefault();
        setTappedOpen(true);
        return;
      }

      if (isSecondTapOnMobile || shouldNavigateOnDesktop) {
        trackViewItemEvent();
        handleNavigate(e);
      }
    },
    [productType, isTouch, tappedOpen, openOptions, handleAddClick, handleNavigate, trackViewItemEvent],
  );

  return (
    <div className={`flex justify-end w-full h-fit ${className || ''}`}>
      <motion.div
        onHoverStart={() => !isTouch && setHovered(true)}
        onHoverEnd={() => {
          setHovered(false);
          if (isTouch) {
            setTappedOpen(false);
          }
        }}
        className="w-full flex items-center"
      >
        <motion.div
          key="expanded"
          className={`flex justify-end items-center w-full gap-2 rounded-[20px] ${openOptions ? 'bg-primary-75' : 'bg-transparent'}`}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {openOptions && (
              <motion.div
                key="minus-group"
                className="flex items-center gap-2 w-full justify-between"
                initial={{ x: 48, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 48, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 36, mass: 0.6 }}
              >
                <Condition
                  expression={productType === ProductType.Variable}
                  then={
                    <p className="text-label-small pl-5 text-primary-400 font-normal text-center w-full">
                      Available Options
                    </p>
                  }
                  else={
                    <>
                      <MotionButton
                        onClick={handleMinusClick}
                        aria-label="Decrease quantity"
                        data-testid={PRODUCT_CARD_ADD_TO_CART_DECREMENT_TEST_ID}
                        className="h-[40px] w-[40px] bg-primary-400 hover:bg-primary-400 text-white rounded-[20px] px-3 shadow-[0_4px_4px_0_rgba(111,63,215,0.25)]"
                        disabled={isLoading || isCartLoading}
                      >
                        <LucideIcon size="sm" name="Minus" className="text-white" />
                      </MotionButton>
                      <Condition
                        expression={isLoading}
                        then={
                          <div className="flex w-full justify-center items-center">
                            <Spinner className="text-primary-500" size="sm" />
                          </div>
                        }
                        else={
                          <p
                            className="text-label-medium text-neutral-700 w-full text-center font-normal"
                            data-testid={PRODUCT_CARD_ADD_TO_CART_QUANTITY_TEST_ID}
                          >
                            {displayQuantity}
                          </p>
                        }
                      />
                    </>
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            variant="primary"
            aria-label="Add to bag"
            data-testid={PRODUCT_CARD_ADD_TO_CART_INCREMENT_TEST_ID}
            className="w-[40px] h-[40px] bg-primary-400 hover:bg-primary-400 text-white rounded-[20px] px-3"
            onClick={handlePlusClick}
            disabled={isLoading || isCartLoading || isOutOfStock}
          >
            <LucideIcon name="Plus" size="sm" className="text-white" />
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ProductCardAddToCart;
